import { useEffect, useRef, useState } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { InsertPlaceholderDropdown } from "@/components/insert-placeholder-dropdown";
import { MessagePlaceholderPreview } from "@/components/message-placeholder-preview";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Archive, BarChart3, ChevronDown, ChevronRight, Loader2, BookMarked, Pencil, Plus, Search, Settings, Trash2, ArrowUp, ArrowDown, GripVertical, Undo2 } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { CannedResponse } from "@shared/schema";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CANNED_TRIGGER_DEFAULT, useCannedTrigger } from "./_shared";



// ─── Canned-response shortcut settings strip ────────────────────────────────
// Lives at the top of the Canned Responses tab. Lets each team member pick
// the single character that opens the inline autocomplete in the reply box
// (default `/`), or disable the shortcut entirely. Persists to localStorage
// via `useCannedTrigger` so the reply box updates immediately.
export function CannedShortcutSettingsStrip() {
  const [trigger, setTrigger] = useCannedTrigger();
  const [draft, setDraft] = useState(trigger);
  useEffect(() => { setDraft(trigger); }, [trigger]);
  const enabled = !!trigger;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-600" data-testid="strip-canned-shortcut">
      <span className="font-medium text-gray-700">Reply box shortcut</span>
      <div className="flex items-center gap-2">
        <Switch
          checked={enabled}
          onCheckedChange={(v) => setTrigger(v ? (draft || CANNED_TRIGGER_DEFAULT) : "")}
          data-testid="switch-canned-shortcut-enabled"
        />
        <span className="text-gray-500">{enabled ? "Enabled" : "Disabled"}</span>
      </div>
      <div className="flex items-center gap-2">
        <Label htmlFor="input-canned-trigger" className="text-xs text-gray-600">Trigger character</Label>
        <Input
          id="input-canned-trigger"
          value={draft}
          maxLength={1}
          disabled={!enabled}
          onChange={(e) => setDraft(e.target.value.slice(0, 1))}
          onBlur={() => {
            const next = draft.slice(0, 1);
            if (next && next !== trigger) setTrigger(next);
            else if (!next && trigger) setDraft(trigger);
          }}
          className="h-7 w-12 text-center font-mono"
          data-testid="input-canned-trigger"
        />
        <span className="text-gray-400">
          Type <code className="rounded bg-gray-200 px-1 py-0.5 font-mono">{trigger || "/"}</code> at the start of a reply (or after a space) to insert a canned response without clicking.
        </span>
      </div>
    </div>
  );
}


// ─── Canned Responses Tab ────────────────────────────────────────────────────
export function CannedResponsesTab({
  canManage,
  toast,
}: {
  canManage: boolean;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"recent" | "usage">("recent");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formBody, setFormBody] = useState("");
  // formCategoryMode: "__none__" = Uncategorized, "__new__" = create inline,
  // any other string = an existing category name.
  const [formCategoryMode, setFormCategoryMode] = useState<string>("__none__");
  const [formCategoryNew, setFormCategoryNew] = useState("");
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const bodyTextareaFocusedRef = useRef(false);
  useEffect(() => {
    if (!dialogOpen) bodyTextareaFocusedRef.current = false;
  }, [dialogOpen]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<CannedResponse | null>(null);
  // Task #2137 — "Active" vs "Archived" view toggle. Archived items are
  // soft-deleted (have `archived_at` set) and hidden from the picker and
  // autocomplete; this view lets hosts restore them or delete them permanently.
  const [view, setView] = useState<"active" | "archived">("active");
  // Task #2141 — bulk selection of canned responses for "Move to category…".
  // Selection is reset whenever the view toggles since archived rows can't
  // be moved (the action is only offered in the active view).
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  useEffect(() => {
    setSelectedIds(new Set());
  }, [view]);

  const listQuery = useQuery<(CannedResponse & { updatedByName?: string })[]>({
    queryKey: ["/api/app/canned-responses"],
    enabled: view === "active",
  });

  const archivedQuery = useQuery<(CannedResponse & { updatedByName?: string })[]>({
    queryKey: ["/api/app/canned-responses/archived"],
    enabled: view === "archived",
  });

  const restoreMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: (id: number) => apiRequest("POST", `/api/app/canned-responses/${id}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/archived"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/usage-summary"] });
      toast({ title: "Canned response restored" });
    },
    onError: (err) => toast({ title: "Error", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" }),
  });
  // Task #2121 — pull the per-owner category metadata (with pinned sort order
  // and live response counts) so the tab can both honor a custom order and
  // render the Manage Categories modal off the same data set.
  const categoryMetaQuery = useQuery<Array<{ name: string; count: number; sortOrder: number | null }>>({
    queryKey: ["/api/app/canned-responses/categories"],
  });
  const sortOrderMap = new Map<string, number>();
  for (const c of categoryMetaQuery.data ?? []) {
    if (c.sortOrder !== null) sortOrderMap.set(c.name, c.sortOrder);
  }
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);

  // Task #2119 — Usage insights panel. Aggregates the per-insert event log
  // server-side into a small summary the team can act on (top 5 templates,
  // who uses them, how many are stale). Collapsible so it stays unobtrusive
  // until the host opens it.
  type UsageSummary = {
    topResponses: Array<{ id: number; title: string; category: string | null; useCount: number; lastUsedAt: string | null }>;
    byUser: Array<{ userId: number; userName: string; useCount: number }>;
    staleCount: number;
    totalUses: number;
    last30DaysUses: number;
  };
  const [insightsOpen, setInsightsOpen] = useState(false);
  const usageQuery = useQuery<UsageSummary>({
    queryKey: ["/api/app/canned-responses/usage-summary"],
    enabled: insightsOpen,
  });

  const archiveStaleMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/app/canned-responses/archive-stale"),
    onSuccess: async (resp) => {
      const data = await (resp as Response).json().catch(() => ({ archived: 0 }));
      const n = Number(data?.archived ?? 0);
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/usage-summary"] });
      toast({
        title: n === 0 ? "No stale responses to archive" : `Archived ${n} stale response${n === 1 ? "" : "s"}`,
      });
    },
    onError: (err) => toast({ title: "Error", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" }),
  });

  const createMutation = useMutation({
    mutationFn: (data: { title: string; body: string; category: string | null }) =>
      apiRequest("POST", "/api/app/canned-responses", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/categories"] });
      setDialogOpen(false);
      toast({ title: "Canned response saved" });
    },
    onError: (err) => toast({ title: "Error", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { title: string; body: string; category: string | null } }) =>
      apiRequest("PATCH", `/api/app/canned-responses/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/categories"] });
      setDialogOpen(false);
      toast({ title: "Canned response updated" });
    },
    onError: (err) => toast({ title: "Error", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" }),
  });

  const bulkMoveMutation = useMutation({
    mutationFn: ({ ids, category }: { ids: number[]; category: string | null }) =>
      apiRequest("POST", "/api/app/canned-responses/bulk-move", { ids, category }),
    onSuccess: async (resp, vars) => {
      const data = await (resp as Response).json().catch(() => ({ updated: vars.ids.length }));
      const n = Number(data?.updated ?? vars.ids.length);
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/categories"] });
      setMoveDialogOpen(false);
      setSelectedIds(new Set());
      const label = vars.category ?? "Uncategorized";
      toast({
        title: `Moved ${n} response${n === 1 ? "" : "s"} to "${label}"`,
      });
    },
    onError: (err) => toast({ title: "Move failed", description: parseErrorMessage(err, "Couldn't move this. Please try again."), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/app/canned-responses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      setDeleteTarget(null);
      toast({ title: "Canned response deleted" });
    },
    onError: (err) => toast({ title: "Error", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" }),
  });

  const activeResponses = listQuery.data ?? [];
  const archivedResponses = archivedQuery.data ?? [];
  const responses = view === "archived" ? archivedResponses : activeResponses;
  const currentQuery = view === "archived" ? archivedQuery : listQuery;
  const searched = search.trim()
    ? responses.filter(
        (r) =>
          String(r.title ?? "").toLowerCase().includes(search.toLowerCase()) ||
          String(r.body ?? "").toLowerCase().includes(search.toLowerCase()) ||
          String(r.category ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : responses;
  const STALE_AFTER_DAYS = 90;
  const staleCutoffMs = Date.now() - STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
  function isStale(r: CannedResponse): boolean {
    if ((r.useCount ?? 0) > 0) return false;
    const createdMs = new Date(r.createdAt).getTime();
    return Number.isFinite(createdMs) && createdMs < staleCutoffMs;
  }
  const filtered = [...searched].sort((a, b) => {
    if (sortBy === "usage") {
      const diff = (b.useCount ?? 0) - (a.useCount ?? 0);
      if (diff !== 0) return diff;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  const { handleItemClick: handleCannedClick } = useRangeSelection(
    filtered.map((r) => r.id),
    selectedIds,
    setSelectedIds,
  );

  // Task #2115 — group by category for the library view. Empty/null becomes
  // "Uncategorized" and always renders last.
  const UNCATEGORIZED = "Uncategorized";
  const groupMap = new Map<string, (CannedResponse & { updatedByName?: string })[]>();
  for (const r of filtered) {
    const key = r.category && r.category.trim() ? r.category.trim() : UNCATEGORIZED;
    const arr = groupMap.get(key);
    if (arr) arr.push(r);
    else groupMap.set(key, [r]);
  }
  // Task #2121 — honor any pinned sort order from the metadata table; fall
  // back to alphabetical for unpinned categories. "Uncategorized" is always
  // last regardless of order.
  const groupEntries = Array.from(groupMap.entries()).sort(([a], [b]) => {
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    const aOrder = sortOrderMap.get(a);
    const bOrder = sortOrderMap.get(b);
    if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder || a.localeCompare(b);
    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;
    return a.localeCompare(b);
  });

  // All known categories from the metadata query (used by both the edit dialog
  // and the bulk-move dialog). Includes empty categories so they appear as
  // valid destinations in the Move dialog.
  const existingCategories = (categoryMetaQuery.data ?? [])
    .map((c) => c.name)
    .sort((a, b) => a.localeCompare(b));

  function toggleCategory(name: string) {
    setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  // Task #2141 — selection helpers.
  function toggleSelected(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectGroup(items: CannedResponse[]) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = items.every((r) => next.has(r.id));
      if (allSelected) {
        for (const r of items) next.delete(r.id);
      } else {
        for (const r of items) next.add(r.id);
      }
      return next;
    });
  }
  const selectedCount = selectedIds.size;
  function clearSelection() {
    setSelectedIds(new Set());
  }

  function openCreate() {
    setDialogMode("create");
    setEditingId(null);
    setFormTitle("");
    setFormBody("");
    setFormCategoryMode("__none__");
    setFormCategoryNew("");
    setDialogOpen(true);
  }

  function openEdit(r: CannedResponse) {
    setDialogMode("edit");
    setEditingId(r.id);
    setFormTitle(r.title);
    setFormBody(r.body);
    const cat = (r.category ?? "").trim();
    if (!cat) {
      setFormCategoryMode("__none__");
      setFormCategoryNew("");
    } else if (existingCategories.includes(cat)) {
      setFormCategoryMode(cat);
      setFormCategoryNew("");
    } else {
      // Category exists on the response but has no metadata row yet (legacy).
      // Treat as a new name pre-filled so the host can confirm or change it.
      setFormCategoryMode("__new__");
      setFormCategoryNew(cat);
    }
    setDialogOpen(true);
  }

  function insertPlaceholder(token: string) {
    const el = bodyTextareaRef.current;
    const hasFocused = bodyTextareaFocusedRef.current;
    if (!el || !hasFocused) {
      const appendPos = formBody.length + token.length;
      setFormBody((prev) => prev + token);
      requestAnimationFrame(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(appendPos, appendPos);
        }
      });
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = formBody.slice(0, start) + token + formBody.slice(end);
    setFormBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function resolveFormCategory(): string | null {
    if (formCategoryMode === "__none__") return null;
    if (formCategoryMode === "__new__") {
      const t = formCategoryNew.trim();
      return t || null;
    }
    return formCategoryMode;
  }

  function handleSave() {
    if (!formTitle.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    if (!formBody.trim()) {
      toast({ title: "Body is required", variant: "destructive" });
      return;
    }
    if (formCategoryMode === "__new__" && !formCategoryNew.trim()) {
      toast({ title: "Enter a category name or choose an existing one", variant: "destructive" });
      return;
    }
    const data = {
      title: formTitle.trim(),
      body: formBody.trim(),
      category: resolveFormCategory(),
    };
    if (dialogMode === "create") {
      createMutation.mutate(data);
    } else if (editingId != null) {
      updateMutation.mutate({ id: editingId, data });
    }
  }

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="flex h-full flex-col" data-testid="tab-content-canned">
      {/* Header row */}
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <BookMarked className="h-4 w-4 text-gray-500 shrink-0" />
        <h2 className="text-sm font-semibold text-gray-800">Canned Responses</h2>
        <p className="text-xs text-gray-500 hidden sm:block">
          Reusable message templates shared with your entire team.
        </p>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
            <Input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-7 text-sm w-48"
              data-testid="input-canned-tab-search"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as "recent" | "usage")}
            className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700"
            data-testid="select-canned-sort"
          >
            <option value="recent">Sort: Most recent</option>
            <option value="usage">Sort: Most used</option>
          </select>
          {canManage && view === "active" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setManageCategoriesOpen(true)}
              data-testid="button-manage-categories"
            >
              <Settings className="h-4 w-4 mr-1" />
              Manage categories
            </Button>
          )}
          {/* Task #2137 — Active / Archived view toggle */}
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 text-xs" data-testid="toggle-canned-view">
            <button
              type="button"
              onClick={() => setView("active")}
              className={`px-2 py-1 rounded ${view === "active" ? "bg-gray-100 font-semibold text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="button-view-active"
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setView("archived")}
              className={`px-2 py-1 rounded ${view === "archived" ? "bg-gray-100 font-semibold text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="button-view-archived"
            >
              Archived{archivedResponses.length > 0 ? ` (${archivedResponses.length})` : ""}
            </button>
          </div>
          {canManage && view === "active" && (
            <Button size="sm" onClick={openCreate} data-testid="button-new-canned-response">
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
          )}
        </div>
      </div>


      {/* Inline shortcut settings strip — per-user, stored in localStorage so
          each team member can set the trigger char that opens the inline
          autocomplete in the reply box. */}
      <CannedShortcutSettingsStrip />

      {/* Task #2119 — Usage Insights panel. Collapsible to keep the library
          view uncluttered; expands into top responses + per-team-member
          breakdown + a one-click "Archive all stale" bulk action. */}
      <div className="border-b border-gray-200 bg-white" data-testid="section-canned-insights">
        <button
          type="button"
          onClick={() => setInsightsOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
          data-testid="button-toggle-canned-insights"
        >
          {insightsOpen ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <BarChart3 className="h-4 w-4 text-blue-600" />
          <span>Usage insights</span>
          <span className="text-xs font-normal text-gray-400">
            See which templates save the most time across your team
          </span>
        </button>
        {insightsOpen && (
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-3" data-testid="panel-canned-insights">
            {usageQuery.isLoading && (
              <div className="flex items-center justify-center py-6 text-sm text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading insights…
              </div>
            )}
            {usageQuery.data && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-md border border-gray-200 bg-white p-3" data-testid="stat-total-uses">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Total inserts</p>
                    <p className="text-xl font-semibold text-gray-900 mt-0.5">{usageQuery.data.totalUses}</p>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-white p-3" data-testid="stat-30day-uses">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Last 30 days</p>
                    <p className="text-xl font-semibold text-gray-900 mt-0.5">{usageQuery.data.last30DaysUses}</p>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-white p-3" data-testid="stat-stale-count">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Possibly stale</p>
                    <p className="text-xl font-semibold text-gray-900 mt-0.5">{usageQuery.data.staleCount}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-md border border-gray-200 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                      Top 5 most-used responses
                    </p>
                    {usageQuery.data.topResponses.length === 0 ? (
                      <p className="text-xs text-gray-400">No inserts yet — usage will appear here once your team starts inserting templates.</p>
                    ) : (
                      <ol className="space-y-1.5" data-testid="list-top-responses">
                        {usageQuery.data.topResponses.map((r, i) => (
                          <li
                            key={r.id}
                            className="flex items-center justify-between gap-2 text-sm"
                            data-testid={`top-response-${r.id}`}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <span className="text-[11px] font-mono text-gray-400 w-4 shrink-0">{i + 1}.</span>
                              <span className="truncate text-gray-800">{r.title}</span>
                              {r.category && (
                                <span className="text-[10px] rounded bg-gray-100 px-1.5 py-0.5 text-gray-600 shrink-0">
                                  {r.category}
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-gray-500 shrink-0">
                              {r.useCount} {r.useCount === 1 ? "use" : "uses"}
                              {r.lastUsedAt && (
                                <> · {new Date(r.lastUsedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</>
                              )}
                            </span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>

                  <div className="rounded-md border border-gray-200 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                      Inserts by team member
                    </p>
                    {usageQuery.data.byUser.length === 0 ? (
                      <p className="text-xs text-gray-400">No per-user data yet. Once teammates insert responses, their counts show up here.</p>
                    ) : (
                      <ul className="space-y-1.5" data-testid="list-by-user">
                        {usageQuery.data.byUser.map((u) => {
                          const max = usageQuery.data!.byUser[0]?.useCount || 1;
                          const pct = Math.max(4, Math.round((u.useCount / max) * 100));
                          return (
                            <li
                              key={u.userId}
                              className="text-sm"
                              data-testid={`by-user-${u.userId}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate text-gray-800">{u.userName}</span>
                                <span className="text-xs text-gray-500 shrink-0">
                                  {u.useCount} {u.useCount === 1 ? "use" : "uses"}
                                </span>
                              </div>
                              <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100">
                                <div
                                  className="h-1.5 rounded-full bg-blue-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                {canManage && usageQuery.data.staleCount > 0 && (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="text-xs text-amber-800">
                      {usageQuery.data.staleCount} response{usageQuery.data.staleCount === 1 ? "" : "s"} with zero inserts in 90+ days.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => archiveStaleMutation.mutate()}
                      disabled={archiveStaleMutation.isPending}
                      data-testid="button-archive-stale"
                    >
                      {archiveStaleMutation.isPending ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Archiving…</>
                      ) : (
                        <><Archive className="h-3.5 w-3.5 mr-1" />Archive all stale</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>


      {/* Task #2141 — Bulk selection action bar (active view only). */}
      {canManage && view === "active" && selectedCount > 0 && (
        <div
          className="flex items-center justify-between gap-3 border-b border-blue-200 bg-blue-50 px-4 py-2"
          data-testid="canned-bulk-actions"
        >
          <p className="text-sm text-blue-900">
            <span className="font-semibold" data-testid="text-canned-selected-count">
              {selectedCount}
            </span>{" "}
            response{selectedCount === 1 ? "" : "s"} selected
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setMoveDialogOpen(true)}
              data-testid="button-bulk-move-canned"
            >
              <Archive className="h-3.5 w-3.5 mr-1" />
              Move to category…
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              data-testid="button-bulk-clear-canned"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Task #2145 — Auto-purge notice for the Archived view. Shown
          persistently (not just in the empty state) so hosts always see
          the 6-month window while reviewing archived items. */}
      {view === "archived" && (
        <div
          className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-800"
          data-testid="banner-archived-purge-note"
        >
          Archived responses are automatically deleted after 6 months. Restore an item to reset the clock.
        </div>
      )}

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {currentQuery.isLoading && (
          <div className="flex items-center justify-center py-16 text-sm text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        )}

        {!currentQuery.isLoading && responses.length === 0 && view === "active" && (
          <div className="flex flex-col items-center justify-center py-20 text-center px-6">
            <BookMarked className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-600 mb-1">No canned responses yet</p>
            <p className="text-xs text-gray-400 mb-4">
              Save frequently-used messages so your team can insert them in one click.
            </p>
            {canManage && (
              <Button size="sm" onClick={openCreate} data-testid="button-new-canned-empty">
                <Plus className="h-4 w-4 mr-1" />
                Add your first response
              </Button>
            )}
          </div>
        )}

        {!currentQuery.isLoading && responses.length === 0 && view === "archived" && (
          <div className="flex flex-col items-center justify-center py-20 text-center px-6" data-testid="empty-archived">
            <Archive className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-600 mb-1">No archived responses</p>
            <p className="text-xs text-gray-400">
              Archived responses appear here. They're hidden from the picker and autocomplete but can be restored anytime.
            </p>
            <p className="text-xs text-gray-400 mt-2" data-testid="text-archived-purge-note">
              Heads up: archived responses are automatically deleted after 6 months. Restoring an item resets the clock.
            </p>
          </div>
        )}

        {!currentQuery.isLoading && responses.length > 0 && filtered.length === 0 && (
          <div className="flex items-center justify-center py-16 text-sm text-gray-400">
            No responses match your search.
          </div>
        )}

        {groupEntries.map(([category, items]) => {
          const isCollapsed = !!collapsed[category];
          const allInGroupSelected = items.length > 0 && items.every((r) => selectedIds.has(r.id));
          const someInGroupSelected = !allInGroupSelected && items.some((r) => selectedIds.has(r.id));
          return (
            <div key={category} data-testid={`canned-category-${category}`}>
              <div className="w-full flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 px-4 py-2 transition-colors">
                {canManage && view === "active" && (
                  <Checkbox
                    checked={allInGroupSelected ? true : someInGroupSelected ? "indeterminate" : false}
                    onCheckedChange={() => toggleSelectGroup(items)}
                    aria-label={`Select all in ${category}`}
                    data-testid={`checkbox-select-category-${category}`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="flex flex-1 items-center gap-2 text-left"
                  data-testid={`button-toggle-category-${category}`}
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                    {category}
                  </span>
                  <span className="text-xs text-gray-400">({items.length})</span>
                </button>
              </div>
              {!isCollapsed && items.map((r) => (
                <div
                  key={r.id}
                  className="flex items-start gap-3 border-b border-gray-100 px-4 py-3 hover:bg-gray-50 transition-colors"
                  data-testid={`canned-row-${r.id}`}
                >
                  {canManage && view === "active" && (
                    <div onClick={(e) => { e.stopPropagation(); handleCannedClick(r.id, e.shiftKey); }}>
                      <Checkbox
                        checked={selectedIds.has(r.id)}
                        onCheckedChange={() => {}}
                        aria-label={`Select ${r.title}`}
                        className="mt-1"
                        data-testid={`checkbox-canned-${r.id}`}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">{r.title}</p>
                      <span
                        className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600"
                        data-testid={`canned-use-count-${r.id}`}
                      >
                        Used {r.useCount ?? 0} {(r.useCount ?? 0) === 1 ? "time" : "times"}
                      </span>
                      {isStale(r) && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700"
                          title={`No uses in over ${STALE_AFTER_DAYS} days since it was created`}
                          data-testid={`canned-stale-${r.id}`}
                        >
                          Possibly stale
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">{r.body}</p>
                    {r.updatedByName && (
                      <p className="text-xs text-gray-400 mt-1">
                        Last edited by {r.updatedByName} ·{" "}
                        {new Date(r.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    )}
                  </div>
                  {canManage && view === "active" && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openEdit(r)}
                        title="Edit"
                        data-testid={`button-edit-canned-${r.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget(r)}
                        title="Delete"
                        data-testid={`button-delete-canned-${r.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                  {canManage && view === "archived" && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => restoreMutation.mutate(r.id)}
                        disabled={restoreMutation.isPending}
                        title="Restore"
                        data-testid={`button-restore-canned-${r.id}`}
                      >
                        <Undo2 className="h-3.5 w-3.5 mr-1" />
                        Restore
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget(r)}
                        title="Delete permanently"
                        data-testid={`button-delete-canned-${r.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="dialog-canned-form"
        >
          <DialogHeader>
            <DialogTitle>{dialogMode === "create" ? "New Canned Response" : "Edit Canned Response"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="canned-title" className="text-sm font-medium mb-1.5 block">
                Title <span className="text-gray-400 font-normal">(shown in picker)</span>
              </Label>
              <Input
                id="canned-title"
                placeholder="e.g. Rent reminder"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                maxLength={120}
                data-testid="input-canned-title"
              />
              <p className="text-xs text-gray-400 mt-1">{formTitle.length}/120</p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">
                Category <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Select value={formCategoryMode} onValueChange={setFormCategoryMode}>
                <SelectTrigger data-testid="select-canned-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Uncategorized</SelectItem>
                  {existingCategories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                  <SelectItem value="__new__">+ Create new category…</SelectItem>
                </SelectContent>
              </Select>
              {formCategoryMode === "__new__" && (
                <div className="mt-2">
                  <Input
                    autoFocus
                    placeholder="e.g. Move-In, Billing, Maintenance"
                    value={formCategoryNew}
                    onChange={(e) => setFormCategoryNew(e.target.value)}
                    maxLength={60}
                    data-testid="input-canned-category-new"
                  />
                  <p className="text-xs text-gray-400 mt-1">{formCategoryNew.length}/60</p>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Leave as "Uncategorized" to keep this response ungrouped.
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="canned-body" className="text-sm font-medium">
                  Message body
                </Label>
                <InsertPlaceholderDropdown onInsert={insertPlaceholder} />
              </div>
              <Textarea
                ref={bodyTextareaRef}
                id="canned-body"
                placeholder={"Hi {{member_first_name}}, just a reminder that…"}
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                onFocus={() => { bodyTextareaFocusedRef.current = true; }}
                rows={6}
                maxLength={5000}
                className="resize-none"
                data-testid="textarea-canned-body"
              />
              <p className="text-xs text-gray-400 mt-1">{formBody.length}/5000 · Use the dropdown above to insert dynamic fields.</p>
              {/* Live authoring preview — substitutes every known {{token}}
                  with SAMPLE_PLACEHOLDER_CONTEXT values so a broken
                  substitution is visible before the response ships. */}
              <MessagePlaceholderPreview content={formBody} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !formTitle.trim() || !formBody.trim()} data-testid="button-save-canned">
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Saving…</> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="dialog-delete-canned">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete canned response?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-canned">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-canned"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!deleteTarget) return;
                deleteMutation.mutate(deleteTarget.id);
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task #2121 — Manage categories modal: bulk rename, reorder, delete-when-empty. */}
      <ManageCategoriesDialog
        open={manageCategoriesOpen}
        onOpenChange={setManageCategoriesOpen}
        toast={toast}
      />

      {/* Task #2141 — Bulk-move dialog. */}
      <MoveCannedResponsesDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        selectedCount={selectedCount}
        existingCategories={existingCategories}
        isPending={bulkMoveMutation.isPending}
        onSubmit={(category) =>
          bulkMoveMutation.mutate({ ids: Array.from(selectedIds), category })
        }
      />
    </div>
  );
}


// ─── Move Canned Responses Dialog (Task #2141) ───────────────────────────────
// Pick an existing category from a dropdown, type a brand-new name, or move
// the selection to "Uncategorized". The parent component owns the selection
// state and the mutation; this dialog is purely the picker UI.
export function MoveCannedResponsesDialog({
  open,
  onOpenChange,
  selectedCount,
  existingCategories,
  isPending,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  selectedCount: number;
  existingCategories: string[];
  isPending: boolean;
  onSubmit: (category: string | null) => void;
}) {
  // "__new__" sentinel = the host wants to type a brand new category name.
  // "__none__" sentinel = move into the Uncategorized bucket.
  const [mode, setMode] = useState<string>("__none__");
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (open) {
      setMode(existingCategories[0] ?? "__none__");
      setNewName("");
    }
  }, [open, existingCategories]);

  function handleSubmit() {
    if (mode === "__none__") {
      onSubmit(null);
      return;
    }
    if (mode === "__new__") {
      const trimmed = newName.trim();
      if (!trimmed) return;
      onSubmit(trimmed);
      return;
    }
    onSubmit(mode);
  }

  const canSubmit =
    !isPending &&
    selectedCount > 0 &&
    (mode !== "__new__" || newName.trim().length > 0) &&
    (mode !== "__new__" || newName.trim().length <= 60);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-move-canned">
        <DialogHeader>
          <DialogTitle>
            Move {selectedCount} response{selectedCount === 1 ? "" : "s"} to…
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Destination category</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="select-move-category">
                <SelectValue placeholder="Pick a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Uncategorized</SelectItem>
                {existingCategories.length > 0 && (
                  <>
                    {existingCategories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </>
                )}
                <SelectItem value="__new__">+ Create new category…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "__new__" && (
            <div>
              <Label htmlFor="move-new-name" className="text-sm font-medium mb-1.5 block">
                New category name
              </Label>
              <Input
                id="move-new-name"
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={60}
                placeholder="e.g. Move-In, Billing"
                data-testid="input-move-new-category"
              />
              <p className="text-xs text-gray-400 mt-1">{newName.length}/60</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} data-testid="button-confirm-move-canned">
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Moving…
              </>
            ) : (
              "Move"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Manage Categories Dialog (Task #2121) ───────────────────────────────────
// Lists every distinct category currently in use plus any "pinned-only"
// metadata rows, with response counts. Renaming a category updates every
// canned_responses row that uses the old name; deleting is only allowed when
// the category has no rows; reordering pins a custom order that overrides
// alphabetical sort in both the tab and the picker popover.
export function ManageCategoriesDialog({
  open,
  onOpenChange,
  toast,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  toast: ReturnType<typeof useToast>["toast"];
}) {
  const queryClient = useQueryClient();
  type Cat = { name: string; count: number; sortOrder: number | null };
  const query = useQuery<Cat[]>({
    queryKey: ["/api/app/canned-responses/categories"],
    enabled: open,
  });

  // Local working copy so the user can reorder + rename without each click
  // round-tripping to the server. We only persist on explicit save / reorder
  // button clicks; the close button reloads from the server.
  const [working, setWorking] = useState<Cat[]>([]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState("");

  // Task #2345 — "Add category" inline UI.
  const [addingCategory, setAddingCategory] = useState(false);
  const [addCategoryName, setAddCategoryName] = useState("");

  // Task #2345 — track which category's delete button showed the inline error.
  const [deleteErrorName, setDeleteErrorName] = useState<string | null>(null);

  useEffect(() => {
    if (open && query.data) {
      setWorking(query.data);
      setEditingIdx(null);
      setEditingValue("");
      setAddingCategory(false);
      setAddCategoryName("");
      setDeleteErrorName(null);
    }
  }, [open, query.data]);

  const renameMutation = useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) =>
      apiRequest("POST", "/api/app/canned-responses/categories/rename", { from, to }),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/categories"] });
      setEditingIdx(null);
      setEditingValue("");
      toast({ title: `Renamed "${vars.from}" to "${vars.to}"` });
    },
    onError: (err) => toast({ title: "Rename failed", description: parseErrorMessage(err, "Couldn't rename this. Please try again."), variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: (order: string[]) =>
      apiRequest("POST", "/api/app/canned-responses/categories/reorder", { order }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/categories"] });
      toast({ title: "Order saved" });
    },
    onError: (err) => toast({ title: "Save failed", description: parseErrorMessage(err, "Couldn't save. Please try again."), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) =>
      apiRequest("DELETE", `/api/app/canned-responses/categories/${encodeURIComponent(name)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/categories"] });
      toast({ title: "Category removed" });
    },
    onError: (err) => toast({ title: "Delete failed", description: parseErrorMessage(err, "Couldn't delete this. Please try again."), variant: "destructive" }),
  });

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) =>
      apiRequest("POST", "/api/app/canned-responses/categories", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/canned-responses/categories"] });
      setAddingCategory(false);
      setAddCategoryName("");
      toast({ title: "Category created" });
    },
    onError: (err) => toast({ title: "Create failed", description: parseErrorMessage(err, "Couldn't create this. Please try again."), variant: "destructive" }),
  });

  function handleAddCategorySave() {
    const trimmed = addCategoryName.trim();
    if (!trimmed) return;
    if (trimmed.length > 60) {
      toast({ title: "Category must be 60 characters or fewer", variant: "destructive" });
      return;
    }
    createCategoryMutation.mutate(trimmed);
  }

  function moveUp(idx: number) {
    if (idx <= 0) return;
    setWorking((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }
  function moveDown(idx: number) {
    setWorking((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setWorking((prev) => {
      const oldIndex = prev.findIndex((c) => c.name === active.id);
      const newIndex = prev.findIndex((c) => c.name === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  function startRename(idx: number) {
    setEditingIdx(idx);
    setEditingValue(working[idx]?.name ?? "");
  }
  function commitRename(idx: number) {
    const from = working[idx]?.name;
    const to = editingValue.trim();
    if (!from || !to || from === to) {
      setEditingIdx(null);
      setEditingValue("");
      return;
    }
    if (to.length > 60) {
      toast({ title: "Category must be 60 characters or fewer", variant: "destructive" });
      return;
    }
    renameMutation.mutate({ from, to });
  }

  function saveOrder() {
    const order = working.map((c) => c.name);
    reorderMutation.mutate(order);
  }

  // True when the local order differs from the server's last-known order.
  const dirty = (() => {
    const server = query.data ?? [];
    if (server.length !== working.length) return false;
    for (let i = 0; i < server.length; i++) {
      if (server[i].name !== working[i].name) return true;
    }
    return false;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-manage-categories">
        <DialogHeader>
          <DialogTitle>Manage categories</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <p className="text-xs text-gray-500 mb-3">
            Rename a category to update every response that uses it. Reorder to control how
            categories appear in the picker and library — leave alone to keep the default
            alphabetical sort.
          </p>
          {query.isLoading && (
            <div className="flex items-center justify-center py-8 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          )}
          {!query.isLoading && working.length === 0 && !addingCategory && (
            <div className="py-4 text-center text-sm text-gray-400" data-testid="manage-categories-empty">
              No categories yet. Use the button below to add one, or set a category on any canned response.
            </div>
          )}
          {working.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={working.map((c) => c.name)} strategy={verticalListSortingStrategy}>
                <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                  {working.map((c, idx) => (
                    <SortableCategoryRow
                      key={c.name}
                      category={c}
                      idx={idx}
                      total={working.length}
                      editingIdx={editingIdx}
                      editingValue={editingValue}
                      setEditingValue={setEditingValue}
                      cancelEditing={() => { setEditingIdx(null); setEditingValue(""); }}
                      moveUp={() => moveUp(idx)}
                      moveDown={() => moveDown(idx)}
                      startRename={() => startRename(idx)}
                      commitRename={() => commitRename(idx)}
                      showDeleteError={deleteErrorName === c.name}
                      onDeleteClick={() => {
                        if (c.count > 0) {
                          setDeleteErrorName(c.name);
                        } else {
                          setDeleteErrorName(null);
                          deleteMutation.mutate(c.name);
                        }
                      }}
                      deleteDisabled={deleteMutation.isPending}
                      renameDisabled={renameMutation.isPending}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
          {/* Task #2345 — Add category inline UI */}
          {addingCategory ? (
            <div className="mt-3 flex items-center gap-2" data-testid="add-category-inline">
              <Input
                autoFocus
                placeholder="New category name"
                value={addCategoryName}
                onChange={(e) => setAddCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleAddCategorySave(); }
                  if (e.key === "Escape") { setAddingCategory(false); setAddCategoryName(""); }
                }}
                maxLength={60}
                className="h-8 text-sm"
                data-testid="input-add-category-name"
              />
              <Button
                type="button"
                size="sm"
                onClick={handleAddCategorySave}
                disabled={!addCategoryName.trim() || createCategoryMutation.isPending}
                data-testid="button-add-category-save"
              >
                {createCategoryMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setAddingCategory(false); setAddCategoryName(""); }}
                data-testid="button-add-category-cancel"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => { setAddingCategory(true); setAddCategoryName(""); }}
              data-testid="button-add-category"
            >
              + Add category
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-manage-categories-close">
            Close
          </Button>
          {working.length > 0 && (
            <Button
              onClick={saveOrder}
              disabled={!dirty || reorderMutation.isPending}
              data-testid="button-manage-categories-save-order"
            >
              {reorderMutation.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Saving…</> : "Save order"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function SortableCategoryRow({
  category,
  idx,
  total,
  editingIdx,
  editingValue,
  setEditingValue,
  cancelEditing,
  moveUp,
  moveDown,
  startRename,
  commitRename,
  showDeleteError,
  onDeleteClick,
  renameDisabled,
  deleteDisabled,
}: {
  category: { name: string; count: number; sortOrder: number | null };
  idx: number;
  total: number;
  editingIdx: number | null;
  editingValue: string;
  setEditingValue: (v: string) => void;
  cancelEditing: () => void;
  moveUp: () => void;
  moveDown: () => void;
  startRename: () => void;
  commitRename: () => void;
  showDeleteError: boolean;
  onDeleteClick: () => void;
  renameDisabled: boolean;
  deleteDisabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: category.name });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const isEditing = editingIdx === idx;
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex flex-col px-3 py-2 bg-white"
      data-testid={`manage-category-row-${category.name}`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none p-1"
          title="Drag to reorder"
          aria-label={`Drag to reorder ${category.name}`}
          {...attributes}
          {...listeners}
          data-testid={`drag-handle-category-${category.name}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex flex-col">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={moveUp}
            disabled={idx === 0}
            title="Move up"
            data-testid={`button-category-up-${category.name}`}
          >
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={moveDown}
            disabled={idx === total - 1}
            title="Move down"
            data-testid={`button-category-down-${category.name}`}
          >
            <ArrowDown className="h-3 w-3" />
          </Button>
        </div>
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                  if (e.key === "Escape") { cancelEditing(); }
                }}
                maxLength={60}
                className="h-7 text-sm"
                data-testid={`input-rename-category-${category.name}`}
              />
              <Button
                type="button"
                size="sm"
                onClick={commitRename}
                disabled={renameDisabled}
                data-testid={`button-rename-save-${category.name}`}
              >
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={cancelEditing}
                data-testid={`button-rename-cancel-${category.name}`}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-800 truncate" data-testid={`text-category-name-${category.name}`}>
                {category.name}
              </span>
              <span className="text-xs text-gray-400" data-testid={`text-category-count-${category.name}`}>
                {category.count} {category.count === 1 ? "response" : "responses"}
              </span>
            </div>
          )}
        </div>
        {!isEditing && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={startRename}
              title="Rename"
              data-testid={`button-edit-category-${category.name}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={onDeleteClick}
              disabled={deleteDisabled}
              title="Delete category"
              data-testid={`button-delete-category-${category.name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      {showDeleteError && (
        <p className="mt-1 ml-16 text-xs text-red-600" data-testid={`delete-error-${category.name}`}>
          Can't delete this category while it still has responses. Move or remove the responses first.
        </p>
      )}
    </li>
  );
}
